import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { AuthService } from '@libs/shared/auth';
import { CapacitorService, EnvironmentService } from '@libs/shared/common';
import { LocalStorageService } from '@libs/shared/services';
import { firstValueFrom } from 'rxjs';
import { compare, valid } from 'semver';

@Injectable({
  providedIn: 'root',
})
export class LiveUpdateService {
  private appVersion: string = '';
  private readonly capacitorService: CapacitorService = inject(CapacitorService);
  private readonly envService: EnvironmentService = inject(EnvironmentService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly localStorageService: LocalStorageService = inject(LocalStorageService);
  private readonly router: Router = inject(Router);
  private updateRunning: boolean = false;
  private readonly liveUpdateUrlStorageKey: string = 'live-update-previous-url';

  public constructor() {
    void this.updateVersion();
  }

  public async checkForAndApplyUpdate(bffVersion: string): Promise<void> {
    if (!bffVersion) {
      return;
    }

    if (!this.capacitorService.isCapacitorApp()) {
      return;
    }

    if (this.envService.type() === 'dev') {
      return;
    }

    const nativeUpdateAvailable: boolean = await firstValueFrom(this.capacitorService.isNativeAppUpdateAvailable());

    if (nativeUpdateAvailable) {
      return;
    }

    // this is to prevent reloading while we log in.
    const userIsAuthorized: boolean = await firstValueFrom(this.authService.isUserAuthorized$());

    if (!userIsAuthorized) {
      return;
    }

    const currentInstalledVersion: string = this.appVersion;

    if (!this.isNewerVersion(currentInstalledVersion, bffVersion)) {
      return;
    }

    if (this.updateRunning) {
      return;
    }

    this.updateRunning = true;

    try {
      const bundleExists: boolean = await this.bundleExists(bffVersion);

      if (!bundleExists) {
        await this.downloadBundle(bffVersion);
      }

      await this.setBundle(bffVersion);
      await this.removeOldBundles(bffVersion);
      await this.reloadWebViewToUseUpdate();
    } catch (ex: unknown) {
      this.appVersion = currentInstalledVersion;

      throw ex;
    } finally {
      this.updateRunning = false;
    }
  }

  public async reloadWebViewToUseUpdate(): Promise<void> {
    if (!this.capacitorService.isCapacitorApp()) {
      return;
    }

    const currentUrl: string = this.router.url;

    this.localStorageService.set(this.liveUpdateUrlStorageKey, currentUrl);

    const message: string = 'Es ist ein Update verfügbar. Dieses wird nach dem Bestätigen installiert und die App wird neu gestartet.';

    if (this.envService.type() !== 'prod') {
      if (!confirm(message)) {

        return;
      }
    } else {
      alert(message);
    }

    await LiveUpdate.reload();
  }

  public async activateCurrentVersion(): Promise<void> {
    if (!this.capacitorService.isCapacitorApp()) {
      return;
    }

    await LiveUpdate.ready();
  }

  public async updateVersion(): Promise<void> {
    if (!this.capacitorService.isCapacitorApp()) {
      return;
    }

    this.appVersion = (await LiveUpdate.getCurrentBundle()).bundleId ?? this.envService.appVersion();
  }

  public async navigateToPreviousUrl(): Promise<void> {
    const url: string | null = this.localStorageService.get(this.liveUpdateUrlStorageKey);

    if (!url) {
      return;
    }

    this.localStorageService.remove(this.liveUpdateUrlStorageKey);

    await this.router.navigateByUrl(url);
  }

  public getVersion(): string {
    return this.appVersion;
  }

  public isNewerVersion(currentInstalledVersion: string, bffVersion: string): boolean {
    if (!valid(currentInstalledVersion)) {
      return true;
    }

    if (!valid(bffVersion)) {
      return false;
    }

    /*
      -1 or negative number, if version is less than base version
      0, if version is equal to base version
      1 or positive number, if version is greater than base version
    */
    return compare(bffVersion, currentInstalledVersion) > 0;
  }

  private async downloadBundle(version: string): Promise<void> {
    await LiveUpdate.downloadBundle({
      url: `${this.envService.liveUpdateUrl() + version}.zip`,
      bundleId: version,
    });
  }

  private async setBundle(version: string): Promise<void> {
    this.appVersion = version;
    await LiveUpdate.setNextBundle({ bundleId: version });
  }

  private async bundleExists(currentVersion: string): Promise<boolean> {
    return (await LiveUpdate.getBundles()).bundleIds.some(x => x === currentVersion);
  }

  private async removeOldBundles(currentVersion: string): Promise<void> {
    const allBundles: string[] = (await LiveUpdate.getBundles()).bundleIds.sort().reverse();

    if (allBundles.length <= 3) {
      return;
    }

    for (let i: number = 3; i < allBundles.length; i++) {
      if (allBundles[i] === currentVersion) {
        continue;
      }

      await LiveUpdate.deleteBundle({ bundleId: allBundles[i] });
    }
  }
}
